import { create, el } from "@dhx/ts-common/dom";
import {
	createFactory,
	Navbar,
	IState,
	IBlock,
	INavbar,
	IButton,
	IInput,
	IImageButton,
	ISeparator,
	ISpacer,
	ITitle,
	ICustomHTML,
	ISelectButton,
	INavItem,
} from "@dhx/ts-navbar";
import { Id, IHandlers } from "@dhx/ts-common/types";
import { extend } from "@dhx/ts-common/core";
import { locate, locateNode } from "@dhx/ts-common/html";
import { tooltip, Position } from "@dhx/ts-message";
import { TreeCollection } from "@dhx/ts-data";

export interface IToolbarConfig {
	css?: string;
	menuCss?: string;
	data?: any[] | TreeCollection<IRibbonElement>;
}

export interface IRibbon extends INavbar {
	config: IToolbarConfig;

	getState(): IState;
	setState(state: IState): void;
}

interface IRibbonButton extends IButton {
	size?: "small" | "medium" | "auto";
}

interface IRibbonImageButton extends IImageButton {
	size?: "small" | "medium" | "auto";
}

interface IRibbonSelectButton extends ISelectButton {
	size?: "small" | "medium" | "auto";
}

interface IRibbonNavItem extends INavItem {
	size?: "small" | "medium" | "auto";
}

export type IRibbonElement =
	| IRibbonButton
	| IInput
	| IRibbonImageButton
	| ISeparator
	| ISpacer
	| ITitle
	| IRibbonSelectButton
	| ICustomHTML
	| IBlock
	| IRibbonNavItem;

export class Ribbon extends Navbar<IRibbonElement> implements IRibbon {
	protected _listeners: IHandlers;
	protected _widgetHeight: number[] = [];
	protected _haveTitle: boolean;

	constructor(element?: string | HTMLElement, config?) {
		super(
			element,
			extend(
				{
					navigationType: "click",
				},
				config
			)
		);
		this._listeners = {
			input: (e: Event) => {
				const id = locate(e);
				this.data.update(id, { value: (e.target as HTMLInputElement).value });
			},
			tooltip: (e: MouseEvent) => {
				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("data-dhx-id");
				const item = this.data.getItem(id);
				if (item.tooltip) {
					tooltip(item.tooltip, {
						node: elem as HTMLElement,
						position: Position.bottom,
					});
				}
			},
		};

		this._currentRoot = null;
		const render = () => this._draw();
		this.mount(element, create({ render }));
	}
	getState(): IState {
		const state = {};
		this.data.eachChild(
			this.data.getRoot(),
			item => {
				if ((item as any).twoState && !item.group) {
					state[item.id] = (item as any).active;
				} else if (item.type === "input" || item.type === "selectButton") {
					state[item.id] = item.value;
				}
			},
			true
		);
		for (const key in this._groups) {
			if (this._groups[key].active) {
				state[key] = this._groups[key].active;
			}
		}
		return state;
	}
	setState(state: IState): void {
		for (const key in state) {
			if (this._groups && this._groups[key]) {
				if (this._groups[key].active) {
					this.data.update(this._groups[key].active, { active: false });
					this._groups[key].active = state[key];
					this.data.update(state[key], { active: true });
				}
			} else {
				const item = this.data.getItem(key);
				if (item.type === "input" || item.type === "selectButton") {
					this.data.update(key, { value: state[key] });
				} else {
					this.data.update(key, { active: state[key] });
				}
			}
		}
	}
	protected _getFactory() {
		return createFactory({
			widget: this,
			defaultType: "navItem",
			allowedTypes: [
				"navItem",
				"button",
				"imageButton",
				"selectButton",
				"customHTML",
				"input",
				"separator",
				"spacer",
				"title",
				"block",

				"customHTMLButton",
			],
			widgetName: "ribbon",
		});
	}
	protected _getMode(item, root): "right" | "bottom" {
		return item.id === root ? "bottom" : "right";
	}
	protected _close(e: MouseEvent): void {
		this._activePosition = null;
		this._currentRoot = null;
		super._close(e);
	}
	protected _draw() {
		this._heightCalculate();

		const itemsHeight = Math.max(...this._widgetHeight);

		return el(
			"ul.dhx_ribbon.dhx_widget",
			{
				"data-dhx-widget-id": this._uid,
				class: this.config.css ? this.config.css : "",
				tabindex: 0,
				onclick: this._handlers.onclick,
				onmousedown: this._handlers.onmousedown,
				oninput: this._listeners.input,
				onmouseover: this._listeners.tooltip,
				_hooks: {
					didInsert: node => {
						node.el.addEventListener(
							"keyup",
							(e: KeyboardEvent) => {
								if (e.which !== 9) {
									return;
								}
								const elem = locateNode(document.activeElement);
								if (!elem) {
									return;
								}

								const id = elem.getAttribute("data-dhx-id");
								const item = this.data.getItem(id);
								if (item.tooltip) {
									tooltip(item.tooltip, {
										node: elem as HTMLElement,
										position: Position.bottom,
										force: true,
									});
								}
							},
							true
						);
					},
				},
			},
			[
				el(
					"li",
					{
						class: "dhx_ribbon-block dhx_ribbon-block--root",
						style: { height: this._haveTitle ? itemsHeight + 24 : itemsHeight },
					},
					[
						el(
							"ul.dhx_ribbon-content.dhx_ribbon-content--full-width",
							{
								style: { height: itemsHeight },
							},
							this.data.map(
								block =>
									block.type === "block"
										? this._drawBlock(block as any, true)
										: this._factory(block),
								this.data.getRoot(),
								false
							)
						),
					]
				),
			]
		);
	}
	protected _setRoot(id: Id) {
		const parentId = this.data.getParent(id);
		if (this.data.getItem(parentId).type === "block") {
			this._currentRoot = id;
		}
	}
	protected _drawBlock(block: IBlock, isFirst?: boolean) {
		if (!block || block.hidden) {
			return null;
		}
		const classNames =
			"dhx_ribbon-block" +
			(" dhx_ribbon-block" + (block.direction === "col" ? "--col" : "--row")) +
			(block.title ? " dhx_ribbon-block--title" : "") +
			(block.css ? " " + block.css : "") +
			(isFirst ? " dhx_ribbon-block--indented" : "");

		const childs = block.items.map(
			child => {
				if (child.type === "block") {
					return this._drawBlock(child as any);
				}
				if (child.type !== "separator" && child.type !== "spacer") {
					return this._factory(child);
				}
				return null;
			},
			block.id,
			false
		);

		return el(
			"li",
			{
				class: classNames,
			},
			[
				el("ul.dhx_ribbon-content", childs),
				block.title
					? el("span.dhx_ribbon-content-label-wrapper", [
							el("span.dhx_ribbon-content-label", block.title),
					  ])
					: null,
			]
		);
	}
	protected _getBlockHeight(block) {
		if (block.hidden) return 0;
		return block.size === "medium" && block.type !== "button" ? 72 : 36;
	}
	protected _heightCalculate(data = this.data) {
		data.forEach(block => {
			let blockHeight;
			if (block.type === "block" && block.direction === "col") {
				blockHeight = block.items.reduce((count, item) => count + this._getBlockHeight(item), 0);
				this._heightCalculate(block.items);
			} else {
				blockHeight = this._getBlockHeight(block);
			}
			if (block.title) {
				this._haveTitle = true;
			}
			this._widgetHeight.push(blockHeight);
		});
	}
}
